import { useContext, useState, useCallback, useEffect, useRef } from 'react'
import { ptBR } from 'date-fns/locale'
import { EventsCalendarContext } from './context'
import { getDate } from '@utils/functions/dates/dates'
import { DateSinglePicker } from './components/DateSinglePicker'
import { KeyboardArrowLeft, KeyboardArrowRight, Topic, LocationOn } from '@mui/icons-material'
import { Stack, CircularProgress } from '@mui/material'
import { Input } from '@components/atoms'
import { formatTime } from '@utils/functions/formatter/formatter'
import {
  isSameDay,
  addDays,
  addHours,
  subDays,
  format,
  isSameHour,
} from 'date-fns'

import {
  Container,
  Header,
  Content,
  CalendarDateTopDisplay,
  CalendarDateTopDatesDisplay,
  CalendarDateCenterDisplay,
  CalendarDateLeftDisplay,
  CalendarDateDay,
  CalendarDateSquareItem,
  EventTitle,
  EventTitleBold,
  EventTime,
  EventDescription,
} from './styles'
import AgendaSwitch from './components/AgendaSwitch'
import { ScheduleContext } from '@pages/private/Schedule/context'
import { useContextSelector } from 'use-context-selector'

function EventsCalendarDay(props) {
  const isLoading = useContextSelector(ScheduleContext, (s) => s.isLoading)
  const {
    data,
    headerLocation,
    handleUpdateSearch,
    selectedDate,
    setSelectedDate,
    tab,
    setTab,
  } = props

  const [textSearch, setTextSearch] = useState('')
  const calendarRef = useRef(null)

  useEffect(() => {
    if (selectedDate)
      setPeriod({
        start: getDate(selectedDate),
        end: null,
      })
  }, [selectedDate])

  const [period, setPeriod] = useState({
    start: getDate(new Date()),
    end: null,
  })

  const { handleOpenModalDetailsEvent } = useContext(EventsCalendarContext)

  const handleOpenEvent = useCallback(
    (id) => {
      const selectedEvent = data.find((event) => event.id === id)
      handleOpenModalDetailsEvent(selectedEvent.event)
    },
    [data]
  )

  const formattedData = data.sort((a, b) => a.start - b.start);

  const handleNextPeriod = useCallback(() => {
    const start = addDays(period.start, 1)
    if (selectedDate) {
      setSelectedDate(start)
      return
    }
    setPeriod({
      start,
      end: null,
    })
  }, [selectedDate, period])

  const handlePreviousPeriod = useCallback(() => {
    const start = subDays(period.start, 1)
    if (selectedDate) {
      setSelectedDate(start)
      return
    }
    setPeriod({
      start,
      end: null,
    })
  }, [selectedDate, period])

  const handleChangePeriod = useCallback((date) => {
    if (!date || isNaN(date)) return
    if (selectedDate) {
      setSelectedDate(date)
      return
    }
    setPeriod({
      start: date,
      end: null,
    })
  }, [selectedDate, period])

  useEffect(() => {
    handleUpdateSearch({
      [`buckets.events.limit`]: 200,
      [`buckets.events.filter.date.from`]: format(period.start, 'yyyy-MM-dd'),
      [`buckets.events.filter.date.to`]: format(period.start, 'yyyy-MM-dd'),
      [`buckets.events.filter.text`]: textSearch,
    })
  }, [period, textSearch])

  useEffect(() => {
    const hourIndex = hours.findIndex((hour) => hour === '7 AM')

    if (calendarRef.current && hourIndex !== -1) {
      const scrollPosition = squareHeight * hourIndex
      calendarRef.current.scrollTop = scrollPosition
    }
  }, [])

  const squareHeight = 66

  const hours = [
    '',
    '1 AM',
    '2 AM',
    '3 AM',
    '4 AM',
    '5 AM',
    '6 AM',
    '7 AM',
    '8 AM',
    '9 AM',
    '10 AM',
    '11 AM',
    '12 PM',
    '1 PM',
    '2 PM',
    '3 PM',
    '4 PM',
    '5 PM',
    '6 PM',
    '7 PM',
    '8 PM',
    '9 PM',
    '10 PM',
    '11 PM',
  ]

  const rows = hours.length

  return (
    <Container>
      <Header location={headerLocation}>
        <Stack direction={'row'}>
          <KeyboardArrowLeft stroke="#606162" onClick={handlePreviousPeriod} />
          <KeyboardArrowRight stroke="#606162" onClick={handleNextPeriod} />
        </Stack>
        <Stack direction="row" spacing={2} style={{ marginRight: 'auto' }}>
          <DateSinglePicker
            date={period.start}
            text={format(period.start, 'dd MMMM', { locale: ptBR })}
            onChange={handleChangePeriod}
          />
          {isLoading && formattedData.length === 0 && <CircularProgress color="primary" size={15} />}
        </Stack>
        {tab && setTab && <AgendaSwitch tab={tab} setTab={setTab}/>}
        {tab && setTab && <Input
          size="tiny"
          type="search"
          id="panel-search"
          label="Pesquisar"
          value={textSearch}
          sx={{ width: '262px',
            [`& .MuiFormLabel-root`]: {
              color: '#606162',
            }}}
          onChange={(e) =>
            setTextSearch(e.target.value)
          }
        />}
      </Header>

      <Content>
        <CalendarDateTopDisplay grid={7}>
          {Array.from(Array(1)).map((_, index) => (
            <CalendarDateTopDatesDisplay
              key={index}
              current={isSameDay(addDays(period.start, index), new Date())}
            >
              <span>
                {format(addDays(period.start, index), 'EEE', {
                  locale: ptBR,
                }).slice(0, 3)}
              </span>

              <span style={{ padding: '4px' }}>
                {format(addDays(period.start, index), 'dd')}
              </span>
            </CalendarDateTopDatesDisplay>
          ))}
        </CalendarDateTopDisplay>

        <CalendarDateCenterDisplay ref={calendarRef}>
            {Array.from(Array(rows)).map((_, index) => (
              <div key={index} style={{display: 'flex', minHeight: '28px'}}>
                <CalendarDateLeftDisplay key={`hour-${index}`}>
                  <span>{hours[index]}</span>
                </CalendarDateLeftDisplay>

                <CalendarDateDay key={`events-${index}`}>
                  {formattedData
                    .filter((item) =>
                      isSameHour(addHours(item.start, 3), addHours(period.start, index)))
                    .map((item, index) => {

                    return (
                      <CalendarDateSquareItem
                        key={`${index}-${item.id}`}
                        background={item.background}
                        borderColor={item.borderColor}
                        onClick={() => handleOpenEvent(item.id)}
                      >
                        <Stack direction="row" sx={{
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          width: "100%",
                        }}>
                          <Stack direction="column" spacing={1} sx={{
                            flex: 1,
                            minWidth: 0,
                            marginRight: 1,
                          }}>
                            <EventTitleBold>{item.title}</EventTitleBold>
                            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                              <LocationOn sx={{ fontSize: 16, flexShrink: 0 }} />
                              <EventTitle>{item.event.local ? item.event.local.name : 'Não informado'}</EventTitle>
                            </Stack>
                            <Stack direction="row" spacing={0.5} justifyContent={"flex-start"}>
                              <Topic sx={{ fontSize: 16, flexShrink: 0 }} />
                              <EventDescription>{item.event.description}</EventDescription>
                            </Stack>
                          </Stack>
                          <EventTime sx={{ 
                            flexShrink: 0,
                            whiteSpace: 'nowrap',
                            alignSelf: 'flex-start',
                          }}>{formatTime(item.start)} BRT</EventTime>
                        </Stack>
                      </CalendarDateSquareItem>
                    )
                  })}
                </CalendarDateDay>
              </div>
            ))}
        </CalendarDateCenterDisplay>
      </Content>
    </Container>
  )
}

export default EventsCalendarDay
